import { useQuery } from "@tanstack/react-query";
import { authService, type DesktopAppStatus, type MobileAppStatus } from "@/services/authService";
import { useAuthStore } from "@/stores/authStore";
import { isAndroid, isIOS, isWindowsDesktop } from "@/lib/platform";

/**
 * 이 PC 에 Windows 메신저가 실행 중/설치돼 있는지 (서버 /auth/me/desktop-app).
 * present 가 true 면 "설치" 버튼을 숨긴다. 조회 실패 시 false → 버튼은 보인다.
 * 서버는 앱 삭제를 알 수 없어 "최근 30분 활동" 으로 설치 여부를 보므로, 삭제 후 최대 30분 뒤 버튼이 돌아온다.
 * Windows 가 아닌 기기는 설치 대상이 아니므로 조회 자체를 하지 않는다(5분 폴링 낭비 방지).
 */
export function useDesktopAppStatus(): { data: DesktopAppStatus | undefined; present: boolean } {
  const user = useAuthStore((s) => s.user);
  const { data } = useQuery({
    queryKey: ["desktop-app-status"],
    queryFn: authService.getDesktopAppStatus,
    enabled: !!user && isWindowsDesktop(),
    staleTime: 1000 * 60,
    refetchInterval: 1000 * 60 * 5, // 앱을 지우거나 켜면 새로고침 없이도 버튼이 따라 바뀌게
    refetchOnWindowFocus: true,
    retry: 1,
  });
  return { data, present: !!data && (data.running_here || data.installed_here) };
}

/**
 * 이 계정으로 모바일 앱이 설치·사용 중인지 (서버 /auth/me/mobile-app, Android 브라우저에서만 조회).
 * 휴대폰 IP 는 수시로 바뀌어 "이 기기" 판정이 불가능하므로 계정 단위(최근 14일 앱 활동) 로 본다.
 */
export function useMobileAppStatus(): { data: MobileAppStatus | undefined; present: boolean } {
  const user = useAuthStore((s) => s.user);
  const { data } = useQuery({
    queryKey: ["mobile-app-status"],
    queryFn: authService.getMobileAppStatus,
    enabled: !!user && isAndroid(),
    staleTime: 1000 * 60,
    refetchInterval: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    retry: 1,
  });
  return { data, present: !!data && data.installed };
}

export type MessengerPlatform = "windows" | "android" | "ios" | "other";

/**
 * 접속 기기의 운영체제에 맞는 메신저 앱 설치 여부 — Windows 는 데스크톱 앱, Android 는 모바일 앱 (대표 지시 2026-10-03).
 * - present: 그 OS 용 앱이 이미 있어 "메신저 설치" 버튼을 숨겨야 함
 * - iOS 는 제공하는 앱이 없어 버튼을 숨기고, 그 외(Mac·Linux)는 QR 로 휴대폰에 설치할 수 있어 보인다
 */
export function useMessengerAppStatus(): {
  platform: MessengerPlatform;
  present: boolean;
  showInstallButton: boolean;
  desktop: DesktopAppStatus | undefined;
  mobile: MobileAppStatus | undefined;
} {
  const platform: MessengerPlatform = isWindowsDesktop() ? "windows" : isAndroid() ? "android" : isIOS() ? "ios" : "other";
  const desktop = useDesktopAppStatus();
  const mobile = useMobileAppStatus();
  const present = platform === "windows" ? desktop.present : platform === "android" ? mobile.present : false;
  return {
    platform,
    present,
    showInstallButton: platform !== "ios" && !present,
    desktop: desktop.data,
    mobile: mobile.data,
  };
}
